<template>
	<div :class="{ 'input-file': true, invalid: invalid, disabled: disabled }">
		<div
			v-if="!hideFileDrop"
			ref="fileDropArea"
			class="file-drop-area"
			:style="{ height: DEFAULT_HEIGHT + 'px' }"
		>
			<div
				v-if="showMetadata"
				class="file-metadata"
			>
				<p><b>File Name:</b> {{ currentDoc.fileName }}</p>
				<p><b>File Type:</b> {{ currentDoc.fileType }}</p>
				<p><b>File Size:</b> {{ currentDoc.fileSize }} KB</p>
			</div>

			<div
				v-else-if="currentDocImgSrc"
				class="file-image-preview"
			>
				<img
					:src="currentDocImgSrc"
					:alt="'File Preview: ' + currentDoc.fileName"
					width="100%"
					@error="onImagePreviewError"
				/>
			</div>

			<div
				v-else-if="folderLoadComplete"
				class="file-structure-display"
			>
				<div
					v-for="(folder, index) in Object.keys(fileSystemForFolder.folders)"
					:key="index"
				>
					<DisplayFolderStructure
						:fileData="fileSystemForFolder.folders[folder]"
						:folderName="folder"
						:rootFolder="true"
					/>
				</div>
			</div>

			<div
				v-else
				class="file-drag-n-drop"
			>
				<i class="fas fa-cloud-upload-alt" />
				<p v-if="!usingTouchScreen">Drag Files to Upload</p>
			</div>
		</div>

		<div
			:class="{
				'upload-buttons': true,
				'capture-available': isCaptureAvailable,
			}"
		>
			<label
				for="fileSelectInput"
				class="button-edit"
			>
				<span class="choose-file-button">
					<i class="fas fa-upload" />
					{{ buttonText ? buttonText : 'Choose File' }}
				</span>

				<input
					id="fileSelectInput"
					ref="fileSelectInput"
					type="file"
					:accept="filesAccepted"
					:directory="uploadDirectory"
					:webkitdirectory="uploadDirectory"
					:mozdirectory="uploadDirectory"
					:disabled="disabled"
					@change="handleInputFileChange"
				/>
			</label>

			<label
				v-if="isCaptureAvailable"
				for="fileCaptureInput"
				class="button-edit button-file-capture"
			>
				<span class="choose-file-button"> <i :class="captureFileIcon" /> {{ captureFileText }} </span>

				<input
					id="fileCaptureInput"
					type="file"
					:accept="filesAccepted"
					:capture="capture"
					:disabled="disabled"
					@change="handleInputFileChange"
				/>
			</label>
		</div>
	</div>
</template>
<script lang="ts">
	export interface IInputFileProps {
		/** A file to use to initialize data in this component */
		initFile?: File;

		/** A directory to use to initialize data in this component. Will not be used if `uploadDirectory` isn't enabled. */
		initDirectory?: IUploadedFiles[];

		/** Whether or not to show an image preview (image files only) or only file metadata */
		showFilePreview?: boolean;

		/** Whether or not to show the file drop area */
		hideFileDrop?: boolean;

		/** Text that show up on the button */
		buttonText?: string;

		/** Removes any language referring to drag-and-drop functionality */
		usingTouchScreen?: boolean;

		/** Must be a MIME type string */
		filesAccepted?: string;

		/** Max size of file in bytes */
		maxSize?: number;

		/** Sets a preview image before any file has been uploaded */
		preloadImageSrc?: string;

		/** On preview image error, image is replaced with this */
		previewFallbackSrc?: string;

		/** User/Environment; if set, will try to open user's camera/microphone */
		capture?: 'user' | 'environment';

		/** Whether or not the uploaded file fails form validation */
		invalid?: boolean;

		/** When true, will disable all input into this field */
		disabled?: boolean;

		/** When true, allows the user to upload an entire directory instead of just a single file */
		uploadDirectory?: boolean;
	}

	export interface IUploadedFiles {
		file: File;
		relativePath?: string[];
	}
</script>
<script setup lang="ts">
	import { computed, nextTick, onBeforeMount, onMounted, ref, useTemplateRef } from 'vue';

	import { EventBusCore } from '@core/services/util';
	import util from '@core/services/util';

	import DisplayFolderStructure from '@core/components/DisplayFolderStructure.vue';

	interface ICurrentDocData {
		fileData?: File;
		fileType?: string;
		fileSize?: number;
		fileName?: string;
	}

	interface IFileSystem {
		folders: {
			[key: string]: IFileSystem;
		};
		files?: File[];
	}

	// #region Constants
	const DEFAULT_HEIGHT = 300;
	// #endregion

	// #region Props & Refs
	const props = defineProps<IInputFileProps>();
	const emit = defineEmits<{
		fileDataChange: [file: File];
		folderDataChange: [files: IUploadedFiles[]];
	}>();

	const fileDropAreaRef = useTemplateRef<HTMLDivElement>('fileDropArea');
	const fileSelectInputRef = useTemplateRef<HTMLInputElement>('fileSelectInput');

	const captureFileText = computed((): string => {
		if (props.filesAccepted.includes('image')) return 'Take Photo';
		else if (props.filesAccepted.includes('video')) return 'Record Video';
		else if (props.filesAccepted.includes('audio')) return 'Record Audio';
		return null;
	});

	const isCaptureAvailable = computed((): boolean => {
		return props.capture && captureFileText.value != null;
	});

	const captureFileIcon = computed((): string => {
		if (props.filesAccepted.includes('image')) return 'fas fa-camera';
		else if (props.filesAccepted.includes('video')) return 'fas fa-video';
		else if (props.filesAccepted.includes('audio')) return 'fas fa-microphone';
		return null;
	});
	// #endregion

	// #region Lifecycle Hooks
	onBeforeMount(() => {
		if (props.initFile) setCurrentFile(props.initFile, true);
		else if (props.uploadDirectory && props.initDirectory) dragDropFiles.value = props.initDirectory;

		if (props.preloadImageSrc) currentDocImgSrc.value = props.preloadImageSrc;
	});

	onMounted(() => {
		nextTick(() => {
			['dragenter', 'dragover'].forEach(event => {
				fileDropAreaRef.value?.addEventListener(event, onDropAreaActive, false);
			});

			['dragleave', 'drop'].forEach(event => {
				const eventType = event as keyof WindowEventMap;
				fileDropAreaRef.value?.addEventListener(eventType, onDropAreaInactive, false);
			});
		});
	});
	// #endregion

	// #region Single Doc Upload Data
	const currentDoc = ref<ICurrentDocData>({
		fileData: null,
		fileType: null,
		fileSize: null,
		fileName: null,
	});

	const currentDocImgSrc = ref<string>(null);

	const showMetadata = computed((): boolean => {
		const fileIsImage = currentDoc.value.fileType && currentDoc.value.fileType.indexOf('image') !== -1;
		return currentDoc.value.fileData && (!props.showFilePreview || !fileIsImage);
	});
	// #endregion

	// #region Multiple Doc Upload Data
	const dragDropFiles = ref<IUploadedFiles[]>([]);
	const folderLoadComplete = ref(false);

	const fileSystemForFolder = computed((): IFileSystem => {
		const fileSystem: IFileSystem = { folders: {} };

		dragDropFiles.value.forEach(file => {
			let pathObject = fileSystem;

			file.relativePath.forEach(folder => {
				if (!pathObject.folders[folder]) pathObject.folders[folder] = { folders: {}, files: [] };

				pathObject = pathObject.folders[folder];
			});

			if (file.file) pathObject.files.push(file.file);
		});

		return fileSystem;
	});

	const resetMultiDocData = () => {
		dragDropFiles.value = [];
		folderLoadComplete.value = false;
	};
	// #endregion

	// #region File Input Handling
	const handleInputFileChange = (e: Event) => {
		resetMultiDocData();

		const files: FileList = (e.target as HTMLInputElement).files;
		if (!files.length) return;

		if (!isSubmittedFileValid(files[0])) return;

		if (props.uploadDirectory) {
			for (let i = 0; i < files.length; i++) {
				const currFile = files.item(i);
				dragDropFiles.value.push({
					file: currFile,
					relativePath: currFile.webkitRelativePath.split('/')?.slice(0, -1),
				});
			}

			folderLoadComplete.value = true;
			emit('folderDataChange', dragDropFiles.value);
		} else {
			setCurrentFile(files[0]);
		}
	};

	const setCurrentFile = (file: File, preventEmit = false) => {
		currentDoc.value.fileData = file;
		currentDoc.value.fileName = file.name;
		currentDoc.value.fileType = file.type;
		currentDoc.value.fileSize = file.size / 1000;

		if (!preventEmit) emit('fileDataChange', currentDoc.value?.fileData);

		if (!props.showFilePreview) return;

		if (currentDocImgSrc.value) window.URL.revokeObjectURL(currentDocImgSrc.value);
		currentDocImgSrc.value = window.URL.createObjectURL(currentDoc.value.fileData);
	};

	const setCurrentDirectory = async (e: DragEvent) => {
		if (!e.dataTransfer.items) return;

		for (const item of e.dataTransfer.items) {
			// NOTE: this method is currently experimental, so types are not yet available
			const fileHandle: FileSystemHandle = await (item as any).getAsFileSystemHandle();

			// Process the dropped folder
			if (fileHandle.kind === 'directory') {
				const path = [fileHandle.name];
				await processFolder(fileHandle as FileSystemDirectoryHandle, path);
			}
		}

		folderLoadComplete.value = true;
		emit('folderDataChange', dragDropFiles.value);
	};

	const processFolder = async (folderHandle: FileSystemDirectoryHandle, path: string[]) => {
		for await (const entry of folderHandle.values()) {
			if (entry.kind === 'file') {
				// Process the file
				const file = await (entry as FileSystemFileHandle).getFile();

				dragDropFiles.value.push({ file, relativePath: path });
			} else if (entry.kind === 'directory') {
				// Recursively process subfolders
				const nextPath = [...path, entry.name];
				await processFolder(entry as FileSystemDirectoryHandle, nextPath);
			}
		}
	};
	// #endregion

	// #region Drag n' Drop Handling
	const onDropAreaActive = (e: Event) => {
		e.preventDefault();
		e.stopPropagation();
		fileDropAreaRef.value?.classList.add('highlighted');
	};

	const onDropAreaInactive = async (e: Event) => {
		e.preventDefault();
		e.stopPropagation();
		resetMultiDocData();

		fileSelectInputRef.value.value = null;

		fileDropAreaRef.value.classList.remove('highlighted');

		if (e.type === 'dragleave') return;

		const dragEvent = e as DragEvent;
		if (!isSubmittedFileValid(dragEvent.dataTransfer.files[0])) return;

		dragEvent.dataTransfer.dropEffect = 'copy';

		if (props.uploadDirectory) {
			await setCurrentDirectory(dragEvent);
		} else {
			setCurrentFile(dragEvent.dataTransfer.files[0]);
		}
	};
	// #endregion

	// #region Other Helper Methods
	const onImagePreviewError = (e: Event) => {
		if (props.previewFallbackSrc) currentDocImgSrc.value = props.previewFallbackSrc;

		EventBusCore.emit('imagePreviewError');
		util.toastr('error', 'Error Loading Image Preview', e);
	};

	const isSubmittedFileValid = (file: File): boolean => {
		if (!isTypeAccepted(file.type)) {
			util.toastr('error', 'Invalid File Type', `This file type is not permitted. File must be the following types: ${props.filesAccepted}`);
			return false;
		}

		if (props.maxSize && file.size >= props.maxSize) {
			util.toastr('error', 'File Size Too Large', `Files cannot be bigger than ${props.maxSize / 1000} KB.`);
			return false;
		}

		return true;
	};

	const isTypeAccepted = (fileType: string): boolean => {
		const acceptedTypes = props.filesAccepted.split(',').map(t => t.trim());
		return acceptedTypes.includes(fileType) || acceptedTypes.includes(fileType.split('/')[0] + '/*') || props.filesAccepted === '*';
	};
	// #endregion
</script>
<style>
	.input-file {
		overflow-wrap: anywhere;
	}

	.input-file .file-structure-display {
		align-self: flex-start;
		width: 100%;
	}

	.input-file .file-drop-area {
		display: flex;
		justify-content: center;
		align-items: center;
		border: 5px dashed var(--border-color);
		transition: all 0.3s ease-in-out;
		position: relative;
		overflow: auto;
	}

	.input-file.invalid .file-drop-area {
		border-color: var(--error-color);
	}

	.input-file.disabled .file-drop-area {
		border: 5px dashed var(--border-color);
		opacity: 50%;
		pointer-events: none;
	}

	.input-file .file-drop-area.highlighted {
		border-color: var(--success-color);
		box-shadow: inset 0 0 5px 1px var(--success-color);
	}

	.input-file .file-drop-area > * {
		pointer-events: none;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	.input-file .file-drop-area .file-metadata {
		padding: 5px;
	}

	.input-file .file-drop-area .file-drag-n-drop {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 1.2em;
		background: rgba(0, 0, 0, 0.2);
		transition: all 0.3s ease-in-out;
		color: var(--text-color);
	}

	.input-file .file-drop-area:hover .file-drag-n-drop {
		background: rgba(0, 0, 0, 0.5);
		transition: background 0.3s ease-in-out;
	}
	.input-file .file-drop-area:hover .file-drag-n-drop .fas {
		color: white;
		transition: all 0.3s ease-in-out;
	}

	.input-file .file-drop-area .file-drag-n-drop i {
		color: var(--disabled-color);
		font-size: 3em;
	}

	.input-file .file-drop-area .file-drag-n-drop p {
		text-align: center;
	}

	.input-file .upload-buttons {
		display: flex;
		text-align: center;
		margin-top: 10px;
		gap: 10px;
	}

	.input-file .upload-buttons > label.button-file-capture {
		filter: hue-rotate(180deg);
	}

	.input-file .upload-buttons > label {
		width: 100%;
		position: relative;
		display: block;
		background-color: var(--main-color);
		color: var(--button-color);
	}

	.input-file .upload-buttons > label:hover {
		background-color: var(--accept-color-hover);
	}

	.input-file .upload-buttons > label:has(input:disabled) {
		box-shadow: inset 0 0 0 100vw var(--button-shadow);
		border: 1px solid var(--button-color-border-disabled);
		cursor: no-drop;
		pointer-events: none;
	}

	.input-file .upload-buttons > label > span {
		width: 100%;
		padding: 10px;
		display: flex;
		gap: 10px;
		align-items: center;
		justify-content: center;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	#fileSelectInput,
	#fileCaptureInput {
		opacity: 0;
	}

	.input-file input {
		width: 100%;
		padding: 10px;
	}
</style>
